import {
  avatarForGender,
  validateAvatarConfig,
  type AvatarConfig,
} from "@guandan/avatar-core";
import type { AvatarMotion } from "@guandan/avatar-renderer";
import type { PublicPlayer } from "@guandan/protocol";

type Seat = PublicPlayer["seat"];

const SEAT_DEFAULT_AVATARS: Readonly<Record<Seat, AvatarConfig>> = {
  0: validateAvatarConfig({
    ...avatarForGender("woman"),
    face: "face-w-clear",
    hair: "hair-w-bob",
    hairColor: "#2d201b",
    outfit: "outfit-w-jade",
    accessory: "accessory-w-watch",
    idle: "idle-w-poised",
  }),
  1: validateAvatarConfig({
    ...avatarForGender("man"),
    face: "face-m-clear",
    hair: "hair-m-side",
    hairColor: "#27201d",
    outfit: "outfit-m-jade",
    accessory: "accessory-m-watch",
    idle: "idle-m-poised",
  }),
  2: validateAvatarConfig({
    ...avatarForGender("woman"),
    face: "face-w-defined",
    hair: "hair-w-wave",
    hairColor: "#241915",
    outfit: "outfit-w-navy",
    accessory: "accessory-w-glasses",
    idle: "idle-w-think",
  }),
  3: validateAvatarConfig({
    ...avatarForGender("man"),
    face: "face-m-defined",
    hair: "hair-m-texture",
    hairColor: "#17191d",
    outfit: "outfit-m-navy",
    accessory: "accessory-m-glasses",
    idle: "idle-m-think",
  }),
};

const MOTION_LABELS: Readonly<Record<AvatarMotion, string>> = {
  idle: "从容候场",
  observe: "正在观察牌局",
  think: "正在思考出牌",
  play: "正在出牌",
  pass: "本轮不出",
  happy: "心情愉快",
  regret: "正在复盘",
  win: "庆祝胜利",
  lose: "结束本局",
  talk: "正在向全桌说话",
};

export interface TableAvatarPresentation {
  readonly player: PublicPlayer;
  readonly relativePosition: Seat;
  readonly config: AvatarConfig;
  readonly source: "saved-self" | "saved-player" | "seat-default";
  readonly sourceLabel: string;
  readonly speaking: boolean;
  readonly motion: AvatarMotion;
  readonly motionLabel: string;
  readonly canvasEligible: boolean;
  readonly accessibleLabel: string;
}

export function defaultAvatarForSeat(seat: Seat): AvatarConfig {
  const config = SEAT_DEFAULT_AVATARS[seat];
  if (!config) throw new Error(`无效牌桌席位：${seat}`);
  return config;
}

export function relativeTablePosition(seat: Seat, selfSeat: Seat | null): Seat {
  if (selfSeat === null) return seat;
  return (seat - selfSeat + 4) % 4;
}

export function buildTableAvatarPresentations({
  players,
  selfSeat,
  currentSeat,
  selfAvatar,
  speakingParticipantIds,
}: {
  players: readonly PublicPlayer[];
  selfSeat: Seat | null;
  currentSeat: Seat | null;
  selfAvatar: AvatarConfig;
  speakingParticipantIds: readonly string[];
}): readonly TableAvatarPresentation[] {
  const speakingIds = new Set(speakingParticipantIds);
  const orderedPlayers = [...players].sort(
    (left, right) =>
      relativeTablePosition(left.seat, selfSeat) -
      relativeTablePosition(right.seat, selfSeat),
  );
  const speakingPlayer = orderedPlayers.find(
    (player) => player.voiceActive || speakingIds.has(player.userId),
  );
  const canvasPlayer =
    speakingPlayer ??
    orderedPlayers.find((player) => player.seat === currentSeat) ??
    orderedPlayers.find((player) => player.seat === selfSeat) ??
    orderedPlayers[0];

  return orderedPlayers.map((player) => {
    const isSelf = selfSeat !== null && player.seat === selfSeat;
    const speaking = player.voiceActive || speakingIds.has(player.userId);
    const motion: AvatarMotion = speaking
      ? "talk"
      : player.seat === currentSeat
        ? "think"
        : isSelf
          ? "idle"
          : "observe";
    const publishedAvatar = player.avatar === null
      ? null
      : validateAvatarConfig(player.avatar);
    const config = publishedAvatar ?? (isSelf ? selfAvatar : defaultAvatarForSeat(player.seat));
    const source = publishedAvatar ? (isSelf ? "saved-self" : "saved-player") : "seat-default";
    const sourceLabel = source === "saved-self"
      ? "我的已保存头像"
      : source === "saved-player"
        ? "玩家已保存公开头像"
        : "电脑席位默认形象";
    const genderLabel = config.gender === "woman" ? "女性" : "男性";
    const sourceDescription = source === "saved-self"
      ? "你的已保存现代角色"
      : source === "saved-player"
        ? `该玩家已保存的${genderLabel}现代角色`
        : `电脑使用${genderLabel}现代席位默认形象`;
    return {
      player,
      relativePosition: relativeTablePosition(player.seat, selfSeat),
      config,
      source,
      sourceLabel,
      speaking,
      motion,
      motionLabel: MOTION_LABELS[motion],
      canvasEligible: player.userId === canvasPlayer?.userId,
      accessibleLabel: `${player.nickname}，${sourceDescription}，${MOTION_LABELS[motion]}`,
    };
  });
}
